import React, { useState, useCallback, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { SimpliPayCheckoutProps, CheckoutStep, CardDetails, TransactionResult } from '../types';
import CardForm from './CardForm';
import PinEntry from './PinEntry';
import OtpEntry from './OtpEntry';
import SuccessScreen from './SuccessScreen';
import '../styles/checkout.css';

// Create a query client for react-query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * SimpliPay Checkout Component
 * 
 * A complete checkout modal for accepting card payments via SimpliPay.
 * 
 * @example
 * ```tsx
 * import { SimpliPayCheckout } from '@simplipayng/checkout';
 * 
 * function App() {
 *   const [isOpen, setIsOpen] = useState(false);
 * 
 *   return (
 *     <SimpliPayCheckout
 *       isOpen={isOpen}
 *       config={{
 *         publicKey: 'pk_test_xxxxx',
 *         environment: 'development'
 *       }}
 *       paymentData={{
 *         email: 'customer@example.com',
 *         phone: '+2348012345678',
 *         amount: 5000,
 *         currency: 'NGN',
 *         transactionRef: 'TXN-123',
 *         customerId: 'CUST-456'
 *       }}
 *       callbacks={{
 *         onSuccess: (result) => console.log('Success:', result),
 *         onError: (error) => console.error('Error:', error),
 *         onClose: () => setIsOpen(false)
 *       }}
 *     />
 *   );
 * }
 * ```
 */
const SimpliPayCheckout: React.FC<SimpliPayCheckoutProps> = ({
  isOpen,
  paymentData,
  config,
  callbacks,
}) => {
  const [step, setStep] = useState<CheckoutStep>('card');
  const [cardDetails, setCardDetails] = useState<CardDetails>({ pan: '', expiryDate: '', cvv: '' });
  const [pin, setPin] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionResponse, setTransactionResponse] = useState<any>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('card');
      setCardDetails({ pan: '', expiryDate: '', cvv: '' });
      setPin('');
      setOtp('');
      setError(null);
      setTransactionResponse(null);
    }
  }, [isOpen]);

  const handleSuccess = useCallback((result: TransactionResult) => {
    setStep('success');
    callbacks.onSuccess(result);
  }, [callbacks]);

  const handleError = useCallback((message: string) => {
    setError(message);
    toast.error(message);
    callbacks.onError({ success: false, message });
  }, [callbacks]);

  const handleClose = useCallback(() => {
    callbacks.onClose();
  }, [callbacks]);

  const goBack = useCallback(() => {
    const stepOrder: CheckoutStep[] = ['card', 'pin', 'otp', 'success'];
    const currentIndex = stepOrder.indexOf(step);
    if (currentIndex > 0) {
      setStep(stepOrder[currentIndex - 1]);
    }
  }, [step]);

  if (!isOpen) return null;

  const renderStep = () => {
    const commonProps = {
      paymentData,
      config,
      cardDetails,
      setCardDetails,
      pin,
      setPin,
      otp,
      setOtp,
      isLoading,
      setIsLoading,
      error,
      setError: handleError,
      transactionResponse,
      setTransactionResponse,
      setStep,
      goBack,
      onClose: handleClose,
      onSuccess: handleSuccess,
    };

    switch (step) {
      case 'card':
        return <CardForm {...commonProps} />;
      case 'pin':
        return <PinEntry {...commonProps} />;
      case 'otp':
        return <OtpEntry {...commonProps} />;
      case 'success':
        return <SuccessScreen {...commonProps} />;
      default:
        return <CardForm {...commonProps} />;
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div className="simplipay-checkout-overlay" onClick={handleClose}>
        <div 
          className="simplipay-checkout-container" 
          onClick={(e) => e.stopPropagation()}
        >
          {renderStep()}
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </QueryClientProvider>
  );
};

export default SimpliPayCheckout;
export { SimpliPayCheckout };
